import { createSelector, createSelectorFactory, defaultMemoize } from '@ngrx/store';

import { OfferItem } from '@recruitee/http/types';
import { LocationSelectors } from '@recruitee/locations/data';
import { createEntityCollectionStore, memoizeSelector } from '@recruitee/ngrx';

import { EntityStoreKey, OfferScope } from '../../types';

const { actions, namespace, selectors, reducer } = createEntityCollectionStore<OfferItem>()(
  EntityStoreKey.JobsShared,
  {
    references: makeRef => [makeRef('locationIds', 'locations', LocationSelectors.byIds)],
    customActions: adapter => ({
      fetchCollection: (state, payload: { scope: OfferScope }) =>
        adapter.markAsPending(state, { scope: payload.scope, entities: [] }),
      fetchSharedCollection: state =>
        adapter.markAsPending(state, { scope: OfferScope.Shared, entities: [] }),
      upsertOne: (state, payload: { data: OfferItem; scope: OfferScope }) =>
        adapter.upsertOne(state, payload.data, { scope: payload.scope }),
      upsertMany: (state, payload: { data: OfferItem[]; scope: OfferScope }) =>
        adapter.upsertMany(state, payload.data, { scope: payload.scope }),
      references: (state, payload: { data: OfferItem[] }) =>
        adapter.updateOrCreateMany(state, payload.data),
    }),
  },
);

// entities
const shared = createSelector(selectors.entities({ scope: OfferScope.Shared }), state => state);

// meta
const sharedScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Shared }),
  state => state,
);

const byIds = memoizeSelector((offerIds: number[]) =>
  createSelectorFactory(defaultMemoize)(
    ...(offerIds || []).map(offerId => selectors.byId(offerId)),
    (...result: OfferItem[]) => result,
  ),
);

const customSelectors = {
  shared,
  sharedScopeIsLoaded,
  byIds,
};

const jobsSharedSelectors = {
  ...selectors,
  ...customSelectors,
};

export {
  jobsSharedSelectors as JobsSharedSelectors,
  actions as JobsSharedActions,
  namespace as JobsSharedNamespace,
  reducer as JobsSharedReducer,
};
